import React, { useEffect, useState } from "react";
import { ISubject, SubjectKind } from "common/types";
import DataTable from "react-data-table-component";
import api from "common/api";
import { Button, Col, Row } from "react-bootstrap";
import DeleteSubjectConfirmation from "./DeleteSubjectConfirmation";
import { BiEditAlt } from "react-icons/bi";
import CreateSubjectModal from "./CreateSubjectModal";
import { toast } from "react-toastify";
import EditSubjectModal from "./EditSubjectModal";
import Spinner from "../../spinner";
import usePageTitle from "common/usePageTitle";

interface ISubjectsPageProps {
  kind: SubjectKind;
}

function SubjectsPage({ kind }: ISubjectsPageProps) {
  const [subjects, setSubjects] = useState<ISubject[]>([]);
  const [createSubjectModalShow, setCreateSubjectModalShow] = useState(false);
  const [editSubjectModalShow, setEditSubjectModalShow] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentEditingSubject, setCurrentEditingSubject] = useState({
    id: 0,
    name: "",
    picture: null,
    prep_price: "",
    prices: [],
    kind: kind,
  });

  usePageTitle(
    `Predmeti admin (${kind === SubjectKind.LESSON ? "poduke" : "pripreme"})`,
  );

  useEffect(() => {
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        const response = await api.get("/subjects/");
        const filteredSubjects = response.data.filter(
          (subject: ISubject) => subject.kind === kind,
        );
        setSubjects(filteredSubjects);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [kind]);

  const deleteSubject = async (id: number) => {
    try {
      await api.delete(`/subjects/${id}/`);
      setSubjects(subjects.filter((subject) => subject.id !== id));
      toast.success("Predmet obrisan");
    } catch (error) {
      console.log(error);
      toast.error("Greška prilikom brisanja predmeta");
    }
  };

  const onSubjectCreate = (subject: ISubject) => {
    setSubjects([...subjects, subject]);
  };

  const onSubjectUpdate = (updatedSubject: ISubject) => {
    setSubjects(
      subjects.map((subject) =>
        subject.id === updatedSubject.id ? updatedSubject : subject,
      ),
    );
    setEditSubjectModalShow(false);
  };

  const getPriceDisplay = (subject: ISubject) => {
    if (kind === SubjectKind.PREPARATION) {
      return `${subject.prep_price} €`;
    } else {
      if (!subject.prices || subject.prices.length === 0) {
        return "Nije postavljena cijena";
      }
      return subject.prices
        .map((p) => `${p.duration} min: ${p.price} €`)
        .join(", ");
    }
  };

  const subjectsColumns: any = [
    {
      name: "Naziv",
      selector: (subject: ISubject) => subject.name,
      sortable: true,
    },
    {
      name: "Cijena",
      selector: (subject: ISubject) => getPriceDisplay(subject),
      sortable: true,
      wrap: true,
      cell: (subject: ISubject) => (
        <div style={{ whiteSpace: "pre-wrap" }}>{getPriceDisplay(subject)}</div>
      ),
    },
    {
      button: true,
      cell: (row: any) => {
        return (
          <>
            <BiEditAlt
              className="me-3"
              style={{ color: "darkgreen", cursor: "pointer" }}
              onClick={() => {
                setCurrentEditingSubject(row);
                setEditSubjectModalShow(true);
              }}
              title={"Promijeni predmet"}
            />

            <DeleteSubjectConfirmation
              onDeleteConfirm={deleteSubject}
              subject={row}
            />
          </>
        );
      },
    },
  ];

  return (
    <div>
      <Row className="align-items-center mb-4">
        <Col>
          <h2>Predmeti</h2>
        </Col>
        <Col xs="auto" xl={5}>
          <Button
            variant="primary"
            onClick={() => setCreateSubjectModalShow(true)}
          >
            Dodaj predmet
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xl={8}>
          <DataTable
            columns={subjectsColumns}
            data={subjects}
            progressPending={loading}
            progressComponent={<Spinner />}
            noDataComponent="Trenutno nema predmeta. Dodajte predmet pritiskom na gumb 'Dodaj predmet'"
          />
        </Col>
      </Row>

      <CreateSubjectModal
        show={createSubjectModalShow}
        onHide={() => setCreateSubjectModalShow(false)}
        onSubjectCreate={onSubjectCreate}
        kind={kind}
      />

      <EditSubjectModal
        show={editSubjectModalShow}
        onHide={() => setEditSubjectModalShow(false)}
        subject={currentEditingSubject}
        onSubjectUpdate={onSubjectUpdate}
      />
    </div>
  );
}

export default SubjectsPage;
